import React, { useState, useEffect } from 'react';
import './PlanList.css';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';
import { useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";

const PlanList = () => {
  const { userData } = useUser();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('auth-token');
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_ADMIN_PLAYING_PLAN_LIST_OLD;
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    const fetchPlans = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: headers,
        });

        const result = await response.json();

        if (result.success) {
          const mappedPlans = result.data.playingPlans.map(plan => {
            const benefitsCount = plan.benefits.length;
            const benefitsText = plan.benefits.map(benefit => benefit.benefit).join(', ');

            return {
              id: plan.id,
              planType: plan.sportsPlanType,
              sportType: plan.sportsType,
              name: plan.name,
              type: plan.type,
              price: `${plan.price}`,
              strikethroughPrice: `${plan.strikeThroughPrice}`,
              gstInclusive: plan.gstInclusive ? 'Yes' : 'No',
              tagline: plan.tagline,
              playingHours: plan.playingHours.toString(),
              maxSlotPerDay: plan.maxSlotBookingPerDay.toString(),
              coinsPerHour: plan.coinsRewardedPerHour.toString(),
              benefitsAdded: `${benefitsCount}`,
              iconUrl: plan.iconUrl,
            };
          });

          setPlans(mappedPlans);
        } else {
          throw new Error(result.success_message || "Failed to fetch plans.");
        }
      } catch (err) {
        setError(err.message);
      }
      finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const headers = [
    { key: 'planType', label: 'Plan Type' },
    { key: 'sportType', label: 'Sport Type' },
    { key: 'name', label: 'Plan Name' },
    { key: 'type', label: 'Type' },
    { key: 'price', label: 'Price' },
    { key: 'strikethroughPrice', label: 'Strikethrough Price' },
    { key: 'gstInclusive', label: 'GST Inclusive' },
    { key: 'tagline', label: 'Tagline' },
    { key: 'playingHours', label: 'Playing Hours' },
    { key: 'maxSlotPerDay', label: 'Max Slot Per Day' },
    { key: 'coinsPerHour', label: 'Coins Per Hour' },
    { key: 'benefitsAdded', label: 'Benefits Added' },
  ];

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(10);
  const navigate = useNavigate();

  const handleUpdate = (updatedPlan) => {
    setPlans(plans.map((plan) => (plan.id === updatedPlan.id ? updatedPlan : plan)));
  };
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });
  
    if (result.isConfirmed) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_ADMIN_DELETE_PLAYING_PLAN_OLD}?planId=${id}`,
          {
            method: "POST",
            headers: {
              'Authorization': `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
  
        if (response.ok) {
          setPlans(plans.filter((plan) => plan.id !== id));
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text:"The plan has been deleted.",
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error!",
            text:"Failed to delete the plan.",
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
          });
        }
      } catch (error) {
        Swal.fire("Error!", "Something went wrong.", "error");
      }
    }
  };
  
  const handlePlanList = () => navigate('/oldPlan');

  const filteredPlans = plans.filter((plan) => {
    const searchLower = searchTerm.toLowerCase();

    return (
      (plan.planType?.toLowerCase().includes(searchLower) || '') ||
      (plan.sportType?.toLowerCase().includes(searchLower) || '') ||
      (plan.name?.toLowerCase().includes(searchLower) || '') ||
      (plan.tagline?.toLowerCase().includes(searchLower) || '')
    );
  });


  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.custom-dropdown-player') && !event.target.closest('.dots-btn-player')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      key: columnKey,
      direction: prev.key === columnKey && prev.direction === "ascending" ? "descending" : "ascending",
    }));
  };
  
  const sortData = (data) => {
    const { key, direction } = sortConfig;
    return [...data].sort((a, b) => {
      const valueA = a[key] || '';
      const valueB = b[key] || '';
  
      if (valueA === '' && valueB !== '') return direction === "ascending" ? 1 : -1;
      if (valueB === '' && valueA !== '') return direction === "ascending" ? -1 : 1;
  
      if (valueA < valueB) return direction === "ascending" ? -1 : 1;
      if (valueA > valueB) return direction === "ascending" ? 1 : -1;
      return 0;
    });
  };
  
  

  const sortedData = sortData(filteredPlans);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  const handleItemsPerPageChange = (event) => {
    setrowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleEdit = (planId) => {
    navigate('/oldPlan', { state: { id: planId } });
  };

  const handleBenefits = (planId) => {
    navigate('/benfit', { state: { planId } });
  };

  const totalEntries = filteredPlans.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  
  const toggleDropdown = (id, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownOpen(prev => (prev === id ? null : id));
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
  };
  
  useEffect(() => {
    const closeOnOutsideClick = () => setDropdownOpen(null);
    document.addEventListener('click', closeOnOutsideClick);
    return () => document.removeEventListener('click', closeOnOutsideClick);
  }, []);
  
  return (
    <div className="enrollment-container">

      <div className="search-create-container">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearch}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>
        <button className="create-button" onClick={handlePlanList}>Create Plan</button>
      </div>
      <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
        <option value={10}>10 items per page</option>
        <option value={50}>50 items per page</option>
        <option value={75}>75 items per page</option>
        <option value={100}>100 items per page</option>
      </select>
      <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

      {loading && <div className="loading-spinner">
        <span>Loading</span>
        <div className="spinner"></div>
      </div>
      }
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredPlans.length === 0 && (
        <div className="error-message">No data available</div>
      )}
      
      <div className="payment-list-scroll-container" style={{ overflowX: 'auto', overflowY: 'visible' }}>
        <table className="payment-list-table">
          <thead>
            <tr>
              {headers.map((header) => (
                <th
                  key={header.key}
                  className="challenges-column"
                  onClick={() => handleSort(header.key)}
                >
                  {header.label}
                  {sortConfig.key === header.key ? (
                    sortConfig.direction === "ascending" ? (
                      <span> ↑</span>
                    ) : (
                      <span> ↓</span>
                    )
                  ) : null}
                </th>
              ))}
              {hasPrivilege(userData, 'PLAYING_PLAN', 'PLANS_LIST_MODIFY') && (<th>Action</th>)}
            </tr>
          </thead>
          <tbody>
            {currentRows.map((plan) => (
              <tr key={plan.id}>
                <td>{plan.planType}</td>
                <td>{plan.sportType}</td>
                <td>{plan.name}</td>
                <td>{plan.type}</td>
                <td>{plan.price}</td>
                <td>{plan.strikethroughPrice}</td>
                <td>{plan.gstInclusive}</td>
                <td>{plan.tagline}</td>
                <td>{plan.playingHours}</td>
                <td>{plan.maxSlotPerDay}</td>
                <td>{plan.coinsPerHour}</td>
                <td>{plan.benefitsAdded}</td>
                {hasPrivilege(userData, 'PLAYING_PLAN', 'PLANS_LIST_MODIFY') && (
  <td className="sport-actions-column">
    <button
      className="ellipsis-button"
      onClick={(e) => toggleDropdown(plan.id, e)}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: '24px',
      }}
    >
      &#x22EE;
    </button>

    {dropdownOpen === plan.id && (
      <div
        className="custom-dropdown-player"
        style={{
          position: 'absolute',
          top: dropdownPos.top,
          left: dropdownPos.left,
          zIndex: 9999,
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          minWidth: '150px',
        }}
      >
        <button
          className="dropdown-item"
          onClick={() => handleEdit(plan.id)}
        >
          Edit
        </button>
        <button
          className="dropdown-item"
          onClick={() => handleDelete(plan.id)}
        >
          Delete
        </button>
        <button
          className="dropdown-item"
          onClick={() => handleBenefits(plan.id)}
        >
          Manage Benefits
        </button>
      </div>
    )}
  </td>
)}


              </tr>
            ))}
          </tbody>
        </table>

      </div>
      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default PlanList;
